import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, FileText, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const SLUGS = ['impressum', 'agb', 'datenschutz', 'widerruf', 'versand'];

const LegalPages = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('impressum');
  const [pages, setPages] = useState({ impressum: '', agb: '', datenschutz: '', widerruf: '', versand: '' });

  useEffect(() => {
    fetchAllPages();
  }, []);

  const fetchAllPages = async () => {
    try {
      const results = await Promise.all(SLUGS.map((slug) => axiosInstance.get(`/legal/${slug}`).catch(() => ({ data: {} }))));
      setPages(Object.fromEntries(SLUGS.map((slug, i) => [slug, results[i].data.content || ''])));
    } catch (error) {
      console.error('Error fetching legal pages:', error);
      toast.error('Fehler beim Laden der Seiten');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/legal/${activeTab}`, {
        content: pages[activeTab]
      });
      toast.success('Erfolgreich gespeichert!');
    } catch (error) {
      console.error('Error saving legal page:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open(`/${activeTab}`, '_blank');
  };

  const tabs = [
    { id: 'impressum', label: 'Impressum', icon: FileText },
    { id: 'agb', label: 'AGB', icon: FileText },
    { id: 'datenschutz', label: 'Datenschutz', icon: FileText },
    { id: 'widerruf', label: 'Widerrufsrecht', icon: FileText },
    { id: 'versand', label: 'Versand & Zahlung', icon: FileText }
  ];

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-center py-12">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-6xl mx-auto">
        <AdminPageHeader 
          title="Rechtliche Seiten" 
          subtitle="Impressum, AGB & Datenschutz, Widerrufsrecht bearbeiten"
          icon={FileText}
        />

        {/* Section: Seiten-Tabs - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF8E0]">
            <FileText className="h-4 w-4 text-[#C0A030]" />
          </div>
          <h2 className="text-base font-semibold text-white">Seiten auswählen</h2>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`legal-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-[#C4A084]'
                }`}
                style={activeTab === tab.id ? { backgroundColor: '#C4A084' } : {}}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Editor Card */}
        <Card className="rounded-none border-[#C4A084]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {tabs.find(t => t.id === activeTab)?.label} bearbeiten
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
              >
                <Eye className="h-4 w-4 mr-2" />
                Vorschau
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="content">Inhalt</Label>
              <p className="text-xs text-gray-500 mb-2">
                Sie können Markdown-Syntax verwenden: # für Überschriften, ** für fett, * für kursiv
              </p>
              <Textarea
                id="content"
                value={pages[activeTab]}
                onChange={(e) => setPages({ ...pages, [activeTab]: e.target.value })}
                rows={20}
                className="font-mono text-sm"
                placeholder="Inhalt eingeben..."
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>💡 Tipp:</strong> Verwenden Sie einfache Textformatierung:
              </p>
              <ul className="text-xs text-blue-700 mt-2 space-y-1">
                <li>• <code className="bg-blue-100 px-1"># Überschrift 1</code> für große Überschrift</li>
                <li>• <code className="bg-blue-100 px-1">## Überschrift 2</code> für Unterüberschrift</li>
                <li>• <code className="bg-blue-100 px-1">**Fett**</code> für fetten Text</li>
                <li>• <code className="bg-blue-100 px-1">*Kursiv*</code> für kursiven Text</li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Speichert...' : 'Speichern'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>⚠️ Wichtig:</strong> Ändern Sie die rechtlichen Texte nur, wenn Sie sich sicher sind. 
            Bei rechtlichen Fragen konsultieren Sie bitte einen Anwalt. Die aktuellen Texte basieren auf 
            Ihrer ursprünglichen Website.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalPages;
